import { Squares2X2Icon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import { Badge, ToggleGroup } from '@/components/ui/daisy';
import { useToggleGroupContext } from '@/components/ui/daisy/toggle-group';
import ExamArea from '@/models/ExamArea';
import { ToggleGroupItemProps } from '../ui/daisy/toggle-group/types';
import { KeyboardKeys } from '@/utils/Keyboard';

export const ALL_AREAS = 'all';

export type ExamAreaFilterValue = number | typeof ALL_AREAS;

type ExamAreasFilterProps = {
  examAreas: ExamArea[];
  value: ExamAreaFilterValue;
  onChange: (value: ExamAreaFilterValue) => void;
};

export default function ExamAreasFilter({
  examAreas,
  value,
  onChange,
}: ExamAreasFilterProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.questions',
  });

  const handleChange = (next: string) =>
    onChange(next === ALL_AREAS ? ALL_AREAS : Number(next));

  return (
    <ToggleGroup value={String(value)} onChange={handleChange}>
      <BadgeItem value={ALL_AREAS} color="success">
        <Squares2X2Icon className="w-4 h-4" />
        {t('allAreas')}
      </BadgeItem>
      {examAreas.map(examArea => (
        <BadgeItem
          key={examArea.id}
          value={String(examArea.id)}
          color="primary"
        >
          {examArea.area.name}
        </BadgeItem>
      ))}
    </ToggleGroup>
  );
}

function BadgeItem({ value, color, children }: ToggleGroupItemProps) {
  const { value: selected, onItemClick } = useToggleGroupContext();
  const isActive = selected === value;

  return (
    <Badge
      variant="soft"
      size="md"
      color={isActive ? color : 'default'}
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      onClick={() => onItemClick(value)}
      onKeyDown={event => {
        if (
          event.key === KeyboardKeys.Enter ||
          event.key === KeyboardKeys.Space
        ) {
          event.preventDefault();
          onItemClick(value);
        }
      }}
      className="cursor-pointer select-none gap-1"
    >
      {children}
    </Badge>
  );
}
